import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie, X } from "lucide-react";
import { useConsent } from "@/lib/consent";

export default function CookieBanner() {
  const { showBanner, customizing, prefs, acceptAll, rejectAll, save, setCustomizing } = useConsent();
  const [local, setLocal] = useState({ essential: true, analytics: prefs.analytics, marketing: false });
  if (!showBanner) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 100, opacity: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="fixed bottom-4 left-4 right-4 md:left-6 md:right-6 md:bottom-6 z-[100] border border-[#ff4b00]/40 bg-[#050505]/95 backdrop-blur-xl"
        role="dialog"
        aria-label="Preferenze cookie"
      >
        <div className="max-w-[1400px] mx-auto p-5 md:p-6">
          {!customizing ? (
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div className="flex items-start gap-4">
                <Cookie size={28} className="text-[#ff4b00] shrink-0 mt-1" />
                <div className="max-w-2xl">
                  <div className="font-display text-xl md:text-2xl mb-1">Cookie & privacy</div>
                  <div className="text-white/70 text-sm">
                    Utilizziamo cookie essenziali per il funzionamento del sito e — solo con il tuo consenso — cookie
                    analitici anonimi per capire come lo utilizzi. Leggi la nostra <Link to="/cookie-policy" className="text-[#ff4b00] hover:underline">Cookie Policy</Link> e{" "}
                    <Link to="/privacy" className="text-[#ff4b00] hover:underline">Privacy Policy</Link>.
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap gap-3 shrink-0">
                <button onClick={rejectAll} data-testid="cookie-reject" className="btn-ghost">Solo essenziali</button>
                <button onClick={() => setCustomizing(true)} data-testid="cookie-customize" className="btn-ghost">Personalizza</button>
                <button onClick={acceptAll} data-testid="cookie-accept-all" className="btn-primary">Accetta tutti</button>
              </div>
            </div>
          ) : (
            <div>
              <div className="flex items-start justify-between mb-6">
                <div>
                  <div className="font-mono-tech text-[10px] text-[#ff4b00] mb-2">◐ Personalizza preferenze</div>
                  <div className="font-display text-xl md:text-2xl">Cookie · granularità</div>
                </div>
                <button onClick={() => setCustomizing(false)} className="btn-ghost p-2" aria-label="Chiudi"><X size={14} /></button>
              </div>
              <div className="space-y-4">
                <Row title="Essenziali" desc="Sempre attivi. Necessari per autenticazione, form e sicurezza del sito." checked disabled />
                <Row title="Analitici" desc="Statistiche anonime di navigazione (Google Analytics 4). Ci aiutano a capire quali contenuti apprezzi di più."
                  checked={local.analytics} onChange={(v) => setLocal({ ...local, analytics: v })} testid="cookie-toggle-analytics" />
                <Row title="Marketing" desc="Nessun cookie di marketing attivo su questo sito." checked={false} disabled />
              </div>
              <div className="flex flex-wrap gap-3 mt-6 justify-end">
                <button onClick={rejectAll} className="btn-ghost">Rifiuta tutti</button>
                <button onClick={() => save(local)} data-testid="cookie-save-prefs" className="btn-primary">Salva preferenze</button>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

const Row = ({ title, desc, checked, disabled, onChange, testid }) => (
  <div className={`flex items-start justify-between gap-6 border border-[#1a1a1a] p-4 ${disabled ? "opacity-70" : ""}`}>
    <div>
      <div className="text-white font-medium">{title}{disabled && checked && <span className="ml-2 font-mono-tech text-[10px] text-[#22c55e]">SEMPRE ATTIVO</span>}</div>
      <div className="text-white/60 text-sm mt-1 max-w-xl">{desc}</div>
    </div>
    <button
      type="button"
      disabled={disabled}
      data-testid={testid}
      onClick={() => onChange && onChange(!checked)}
      className={`w-12 h-7 rounded-full border relative transition-colors shrink-0 ${checked ? "bg-[#22c55e] border-[#22c55e]" : "bg-[#111] border-[#333]"} ${disabled ? "cursor-not-allowed" : ""}`}
    >
      <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-transform ${checked ? "translate-x-6" : "translate-x-1"}`} />
    </button>
  </div>
);
